import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SessionsWeek } from '../../sessions/components/SessionsWeek';
import { DayTimelineGrid } from './DayTimelineGrid';
import { useSessionsData } from '../../sessions/hooks/useSessionsData';
import type { Psychologist } from '../../../../../types/psychologist';

interface DayViewProps {
  psychologist: Psychologist | null;
  onPatientClick: (patientId: string, processId?: string, patientName?: string, therapyType?: string) => void;
  onSchedule?: (patientName?: string) => void;
  onOpenSessionModal?: (session: import('../../../../../types/session-lite').SessionLite) => void;
  /** `false` oculta la acción de agendar (app mobile: solo lectura). Por defecto la vista es editable, como en la web. */
  canSchedule?: boolean;
  /** Exposes the hook's goToToday so CalendarTab can wire the "Hoy" button without URL-param gymnastics. */
  goToTodayRef?: React.MutableRefObject<(() => void) | null>;
}

export const DayView: React.FC<DayViewProps> = ({
  onPatientClick,
  onSchedule,
  onOpenSessionModal,
  canSchedule = true,
  goToTodayRef,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get('date');

  const {
    sessionsLoaded,
    isLoading,
    weekDays,
    sessionsForDay,
    selectedISO,
    setSelectedISO,
    onPrevWeek,
    onNextWeek,
    onToday: hookOnToday,
    onViewProcess,
  } = useSessionsData();

  // Expose the hook's onToday to CalendarTab so the "Hoy" button can call it directly,
  // avoiding the navTargetRef mechanism (which flashes intermediate days when hopping weeks).
  useEffect(() => {
    if (goToTodayRef) {
      goToTodayRef.current = hookOnToday;
    }
  }, [goToTodayRef, hookOnToday]);

  // Tracks the date we need to navigate to when it arrives from an external URL change
  // (e.g. MonthView day-number click, or deep-link). NOT used for the "Hoy" button.
  const navTargetRef = useRef<string | null>(dateParam);

  // When the URL ?date param changes externally, record it as the navigation target.
  // Calling setSelectedISO directly here would be overridden by the weekDays useMemo
  // in useSessionsData if that date is outside the currently loaded week.
  useEffect(() => {
    if (dateParam && dateParam !== selectedISO) {
      navTargetRef.current = dateParam;
    }
  }, [dateParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate week-by-week until the target date is visible in weekDays, then select it.
  // Each onPrevWeek / onNextWeek call updates weekStart inside useSessionsData, which
  // recomputes weekDays, which re-triggers this effect — one hop per render cycle.
  useEffect(() => {
    const target = navTargetRef.current;
    if (!target || !weekDays.length) return;

    const inCurrentWeek = weekDays.some((d) => d.dateISO === target);
    if (inCurrentWeek) {
      setSelectedISO(target);
      navTargetRef.current = null;
      return;
    }

    const targetTime = new Date(target + 'T12:00:00').getTime();
    const weekStartTime = new Date(weekDays[0].dateISO + 'T12:00:00').getTime();
    if (targetTime < weekStartTime) {
      onPrevWeek();
    } else {
      onNextWeek();
    }
  }, [weekDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync selectedISO → ?date for internal navigation (week arrows, day pills).
  // Suppressed while navTargetRef is set to avoid URL flicker during week hops.
  useEffect(() => {
    if (!selectedISO) return;
    if (navTargetRef.current) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', 'day');
        next.set('date', selectedISO);
        return next;
      },
      { replace: true },
    );
  }, [selectedISO, setSearchParams]);

  if (!sessionsLoaded) {
    return (
      <div data-testid="day-view-loading" className="mt-4">
        <div className="flex items-center gap-1 mb-6 bg-white rounded-2xl p-2 border border-gray-100 shadow-sm">
          <div className="w-8 h-8 skeleton-shimmer rounded-full flex-shrink-0" />
          {[...Array(7)].map((_, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1 py-1">
              <div className="h-2.5 w-4 skeleton-shimmer rounded" />
              <div className="w-9 h-9 skeleton-shimmer rounded-full" />
              <div className="h-2 w-4 skeleton-shimmer rounded" />
            </div>
          ))}
          <div className="w-8 h-8 skeleton-shimmer rounded-full flex-shrink-0" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="day-view" className="text-left">
      <div id="day-view-week-selector">
        <SessionsWeek
          days={weekDays}
          onPrevWeek={onPrevWeek}
          onNextWeek={onNextWeek}
          selectedISO={selectedISO}
          onSelect={setSelectedISO}
          disabled={isLoading}
        />
      </div>

      <div className="mt-6">
        {isLoading ? (
          <div>
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-4 mb-3 bg-white rounded-xl border border-gray-100"
              >
                <div className="w-10 h-10 skeleton-shimmer rounded-full flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="h-4 skeleton-shimmer rounded w-40 mb-2" />
                  <div className="h-3 skeleton-shimmer rounded w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <DayTimelineGrid
            sessions={sessionsForDay}
            onViewSession={(session) => onViewProcess(session.id, onPatientClick)}
            onOpenSessionModal={onOpenSessionModal}
            onSchedule={onSchedule}
            canSchedule={canSchedule}
            dayISO={selectedISO}
          />
        )}
      </div>
    </div>
  );
};
